import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessagePosition, MessageType } from '@epicuro-next/ataraxia/snack-bar';
import {
  PulseConfigurationKeys,
  PulseConfigurations,
  PULSE_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/pulse-rules';
import { ProductListExportApiService } from '@epicuro-next/data/engagement/engagement-api';
import {
  ProductListExportToEmailBody,
  ProductListExportToSheetRequest,
} from '@epicuro-next/data/engagement/engagement-models';
import { List } from '@epicuro-next/models/engagement';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import { selectActiveBasketAccounts } from '@epicuro-next/state/basket-state';
import {
  selectListEntityFactory,
  selectListEntitySearchSortType,
  updateEntityList,
} from '@epicuro-next/state/list-entities';
import { selectPresetValue } from '@epicuro-next/state/p-basket-state';
import { showSnackBarMessage } from '@epicuro-next/state/snackbar';
import {
  selectShowWholeSalePrices,
  selectUser,
} from '@epicuro-next/state/user-state';
import { getFilenameFromResponseHeader } from '@epicuro-next/utilities';
import { downloadFile } from '@epicuro-next/utilities-browser';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { SearchSortType } from 'graphql-api/gql-api-interfaces';
import { PulseOrderType } from 'models-common';
import { forkJoin, of } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  map,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import * as actions from '../../actions';
import {
  selectGroupExportProducts,
  selectOpenedList,
  selectOpenedListProductReferences,
  selectProductList,
} from '../../selectors';

@Injectable()
export class ExportEffects {
  public exportProductListToPdf$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.exportProductListToPdf),
      switchMap((action) => {
        return forkJoin([
          this.store.pipe(select(selectShowWholeSalePrices), take(1)),
          this.store.pipe(
            select(selectProductList(action.productListId)),
            take(1),
          ),
          this.store.pipe(select(selectGroupExportProducts), take(1)),
        ]);
      }),
      switchMap(([isShowWholeSalePrices, list, groupByCategory]) => {
        return this.productListExportService
          .exportToPdf({
            productListId: (list as List).id,
            groupByCategory,
            isShowWholeSalePrices,
          })
          .pipe(
            switchMap((data: Blob) => {
              return [
                actions.downloadProductListPdf({
                  listName: (list as List).name,
                  data,
                }),
                actions.exportProductListToPdfSuccess(),
              ];
            }),
            catchError((error) =>
              of(actions.exportProductListToPdfFail({ error })),
            ),
          );
      }),
    ),
  );

  public downloadProductListToPdf$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.downloadProductListPdf),
      switchMap((payload) =>
        of(payload).pipe(
          map(({ listName, data }: { listName: string; data: Blob }) => {
            downloadFile(data, `${listName}.pdf`);
            return actions.downloadProductListPdfSuccess();
          }),
          catchError(() => of(actions.downloadProductListPdfFail())),
        ),
      ),
    ),
  );

  public exportProductListToEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.exportProductListToEmail),
      concatLatestFrom((action) => {
        return [
          this.store.pipe(select(selectUser)),
          this.store.pipe(select(selectProductList(action.productListId))),
          this.store.pipe(
            select(
              selectConfigurationValueFrom<
                PulseConfigurationKeys.GTMEcommerceEventsEnabled,
                PulseConfigurations
              >(
                PULSE_FEATURE_NAME,
                PulseConfigurationKeys.GTMEcommerceEventsEnabled,
                false,
              ),
            ),
          ),
        ];
      }),
      switchMap(([action, user, list, gtmEnabled]) => {
        const recipients = action.emailData.recipients.map(
          ({ name, email }) => ({ name: name ? name : email, email }),
        );
        const configuration: Record<string, string> = {};

        if (gtmEnabled) {
          configuration.utm_source = 'shared wishlist';
          configuration.utm_medium = 'email';
          configuration.utm_campaign = list?.name as string;
        }

        if (action.showGetInTouchButton) {
          configuration.getInTouchEmail = user?.email as string;
        }

        return this.productListExportService
          .exportToEmail({
            productListId: action.productListId,
            payload: {
              ...action.emailData,
              recipients,
              configuration,
            } as ProductListExportToEmailBody,
          })
          .pipe(
            map(() =>
              actions.exportListToEmailSuccess({
                productListId: action.productListId,
                emailData: action.emailData,
              }),
            ),
            catchError(() => of(actions.exportListToEmailFail())),
          );
      }),
    ),
  );

  public exportListToEmailFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.exportListToEmailFail),
      map(() => {
        const message = $localize`:ProductLists | Send email failed@@exportListToEmailFailPLists:Send by email failed`;
        return showSnackBarMessage({
          message,
          position: MessagePosition.BOTTOM,
          messageType: MessageType.DANGER,
        });
      }),
    ),
  );

  public updateListEntityOnExportListToEmailSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.exportListToEmailSuccess),
      filter(({ emailData }) => !!emailData.recipients.length),
      concatLatestFrom(({ productListId }) => [
        this.store.select(selectListEntityFactory(productListId)),
      ]),
      filter(([, listEntity]) => !!listEntity?.list),
      map(([{ emailData }, listEntity]) => {
        const recipients = [
          ...new Set(
            ...[
              ...(listEntity?.list?.recipients?.emails ?? []),
              ...emailData.recipients.map((item) => item.email),
            ],
          ),
        ];

        return updateEntityList({
          list: {
            ...listEntity?.list,
            recipients: {
              count: recipients.length,
              emails: recipients,
            },
          } as List,
        });
      }),
    ),
  );

  public exportListToEmailSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.exportListToEmailSuccess),
      map(() => {
        const message = $localize`:ProductLists | Email was successfully sent@@exportListToEmailSuccessSentPLists:Email was successfully sent`;
        return showSnackBarMessage({
          message,
          position: MessagePosition.BOTTOM,
          messageType: MessageType.SUCCESS,
        });
      }),
    ),
  );

  public exportProductListToPdfNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.exportProductListToPdf),
      map(() => {
        const message = $localize`:ProductLists | Generating a PDF for you@@exportProductListToPdfNotification:Generating a PDF for you, download will start in a few seconds`;
        return showSnackBarMessage({
          message,
          position: MessagePosition.BOTTOM,
          messageType: MessageType.INFO,
          hideDelay: 15000,
        });
      }),
    ),
  );

  public exportProductListToSheetNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.exportProductListToSheet),
      map(() => {
        const message = $localize`:ProductLists | Generating a excel file for you@@exportProductListToSheetNotification:Generating a excel file for you, download will start in a few seconds`;
        return showSnackBarMessage({
          message,
          position: MessagePosition.BOTTOM,
          messageType: MessageType.INFO,
          hideDelay: 15000,
        });
      }),
    ),
  );

  public exportProductListImagesNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.exportProductListImages),
      map(() => {
        const message = $localize`:ProductLists | Generating an archive for you@@exportProductListToImagesNotification:Generating an images archive for you, download will start in a few seconds`;
        return showSnackBarMessage({
          message,
          position: MessagePosition.BOTTOM,
          messageType: MessageType.INFO,
          hideDelay: 15000,
        });
      }),
    ),
  );

  public downloadProductListToPdfZeroItemsWarning$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.exportProductListToPdf),
      switchMap(({ productListId }) =>
        this.store.pipe(select(selectProductList(productListId)), take(1)),
      ),
      filter((list) => list?.amount === 0),
      // give enough time to user to grasp first message generated by exportProductListToPdfNotification$
      delay(3000),
      map(() =>
        showSnackBarMessage({
          message: $localize`:ProductLists | Warning List has 0 available products@@exportListToPdfWarningZeroPListsM:Warning: List has 0 available products`,
          position: MessagePosition.BOTTOM,
          messageType: MessageType.WARNING,
        }),
      ),
    ),
  );

  public exportProductListToPdfFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.exportProductListToPdfFail),
      map(({ error }) => {
        let message = $localize`:ProductLists | Export product list to PDF fail@@exportProductListToPdfFail:Export product list to PDF fail`;
        if (error?.status && error?.status >= 500 && error?.error?.size) {
          message = $localize`:ProductLists | Export product list to PDF fail, file size@@exportProductListToPdfFailFileSize:Export product list to PDF fail, file is too big`;
        }
        return showSnackBarMessage({
          message,
          position: MessagePosition.BOTTOM,
          messageType: MessageType.DANGER,
        });
      }),
    ),
  );

  public exportProductListToSheet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.exportProductListToSheet),
      concatLatestFrom(() => [
        this.store.pipe(select(selectActiveBasketAccounts)),
        this.store.pipe(select(selectPresetValue)),
        this.store.pipe(select(selectOpenedListProductReferences)),
        this.store.pipe(select(selectGroupExportProducts), take(1)),
        this.store.pipe(select(selectListEntitySearchSortType())),
      ]),
      switchMap(
        ([
          ,
          activeBasketAccounts,
          currentPresetValue,
          productReferences,
          groupExportProducts,
          sortField,
        ]) => {
          const orderType = Object.values(PulseOrderType).find(
            (value) =>
              currentPresetValue?.toLowerCase() === value.toLowerCase(),
          );

          const exportPayload: ProductListExportToSheetRequest = {
            accountIds: activeBasketAccounts.map(
              (account) => account.accountId,
            ),
            orderType: orderType ?? PulseOrderType.Any,
            productReferences,
          };

          if (groupExportProducts) {
            exportPayload.sortField = sortField as SearchSortType | undefined;
            exportPayload.sortDirection = 0;
          }

          return this.productListExportService
            .exportToSheet(exportPayload)
            .pipe(
              map((response: HttpResponse<Blob>) =>
                actions.downloadProductList({
                  data: response.body as Blob,
                  fileName: getFilenameFromResponseHeader(response.headers),
                }),
              ),
              catchError((error) =>
                of(actions.exportProductListToSheetFailure({ error })),
              ),
            );
        },
      ),
    ),
  );

  public exportProductListImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.exportProductListImages),
      concatLatestFrom(() => [
        this.store.pipe(select(selectActiveBasketAccounts)),
        this.store.pipe(select(selectOpenedListProductReferences)),
      ]),
      switchMap(([, activeBasketAccounts, productReferences]) =>
        this.productListExportService
          .exportToImages({
            accountIds: activeBasketAccounts.map(
              (account) => account.accountId,
            ),
            productReferences,
          })
          .pipe(
            map((response: HttpResponse<Blob>) =>
              actions.downloadProductList({
                data: response.body as Blob,
                fileName: getFilenameFromResponseHeader(response.headers),
              }),
            ),
            catchError((error) =>
              of(actions.exportProductListImagesFailure({ error })),
            ),
          ),
      ),
    ),
  );

  public downloadProductList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.downloadProductList),
      withLatestFrom(this.store.select(selectOpenedList)),
      map(([{ data, fileName }, openedList]) => {
        downloadFile(
          data,
          formatSheetName((openedList as List).name, fileName.extension),
        );
        return actions.downloadProductListSuccess();
      }),
    ),
  );

  public exportProductListToSheetFailureNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.exportProductListToSheetFailure),
      map(({ error }) => {
        let message = $localize`:ProductLists | Export product list to excel failure@@exportProductListToSheetFail:Export product list to excel fail`;
        if (error?.status && error?.status >= 500 && error?.error?.size) {
          message = $localize`:ProductLists | Export product list to excel failure, file size@@exportProductListToSheetFailFileSize:Export product list to excel fail, file is too big`;
        }
        return showSnackBarMessage({
          message,
          position: MessagePosition.BOTTOM,
          messageType: MessageType.DANGER,
        });
      }),
    ),
  );

  public exportProductListImagesFailureNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.exportProductListImagesFailure),
      map(({ error }) => {
        let message = $localize`:ProductLists | Export product list images failure@@exportProductListImagesFail:Export product list to images failed`;
        if (error?.status && error?.status >= 500 && error?.error?.size) {
          message = $localize`:ProductLists | Export product list to images failure, file size@@exportProductListToImagesFailFileSize:Export product list to images fail, file is too big`;
        }
        return showSnackBarMessage({
          message,
          position: MessagePosition.BOTTOM,
          messageType: MessageType.DANGER,
        });
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private productListExportService: ProductListExportApiService,
    private store: Store,
  ) {}
}

export function formatSheetName(name: string, extension: string) {
  return `${name.toLowerCase().replace(/ /gi, '_')}.${extension}`;
}
